import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/utils/axios";
import cls from "./ClubActivities.module.scss";

const ACTION_TYPES = [
  "",
  "role_assigned",
  "role_changed",
  "status_active",
  "status_removed",
  "status_banned",
  "custom",
];

const VISIBILITY = ["", "admin", "club", "public"];

const ClubActivities = () => {
  const { cityId, streetId, clubId } = useParams();

  const backToClubPath = `/admin/cities/single/${cityId}/streets/${streetId}/clubs/${clubId}`;

  const base = useMemo(
    () =>
      `/v1/admin/cities/${cityId}/streets/${streetId}/clubs/${clubId}/activities`,
    [cityId, streetId, clubId]
  );

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  const [page, setPage] = useState(1);
  const [limit] = useState(20);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [actionType, setActionType] = useState("");
  const [visibility, setVisibility] = useState("");

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      setErr("");

      const params = {
        page,
        limit,
        ...(from && { from }),
        ...(to && { to }),
        ...(actionType && { actionType }),
        ...(visibility && { visibility }),
      };

      const { data } = await api.get(base, { params });

      setRows(data.activities || []);
      setMeta(data.meta || null);
    } catch (e) {
      setErr(
        e?.response?.data?.message || e.message || "Failed to load activities"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [base, page, limit, from, to, actionType, visibility]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return (
    <div className={cls.page}>
      {/* ---------- Header ---------- */}
      <header className={cls.header}>
        <div className={cls.headerLeft}>
          <Link to={backToClubPath} className={cls.backLink}>
            ← Back to Club
          </Link>

          <p className={cls.eyebrow}>Admin · Club</p>
          <h1 className={cls.title}>Club Activities</h1>
          <p className={cls.sub}>
            Audit log of all important actions inside this club.
          </p>
        </div>

        <div className={cls.headerRight}>
          <button
            className="nu-btn nu-btn--ghost"
            onClick={fetchActivities}
            disabled={loading}
          >
            Refresh
          </button>
        </div>
      </header>

      {err && <div className={cls.alertErr}>⚠ {err}</div>}

      {/* ---------- Filters ---------- */}
      <div className={cls.toolbar}>
        <div className={cls.filters}>
          <label>
            <span>From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={cls.input}
            />
          </label>

          <label>
            <span>To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={cls.input}
            />
          </label>

          <label>
            <span>Action</span>
            <select
              className={cls.select}
              value={actionType}
              onChange={(e) => setActionType(e.target.value)}
            >
              {ACTION_TYPES.map((a) => (
                <option key={a} value={a}>
                  {a || "All"}
                </option>
              ))}
            </select>
          </label>

          <label>
            <span>Visibility</span>
            <select
              className={cls.select}
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
            >
              {VISIBILITY.map((v) => (
                <option key={v} value={v}>
                  {v || "All"}
                </option>
              ))}
            </select>
          </label>
        </div>

        {meta && (
          <div className={cls.metaPill}>
            Total: <strong>{meta.total}</strong>
          </div>
        )}
      </div>

      {/* ---------- List ---------- */}
      <div className={cls.card}>
        {loading ? (
          <div className={cls.loading}>Loading activities…</div>
        ) : rows.length === 0 ? (
          <div className={cls.empty}>No activities found.</div>
        ) : (
          <ul className={cls.list}>
            {rows.map((a) => (
              <li key={a.activityId} className={cls.row}>
                <div className={cls.avatarWrap}>
                  {a.actor?.avatarThumb?.url ? (
                    <img
                      src={a.actor.avatarThumb.url}
                      alt={a.actor.username}
                      className={cls.avatar}
                    />
                  ) : (
                    <div className={cls.avatarFallback} />
                  )}
                </div>

                <div className={cls.main}>
                  <div className={cls.topRow}>
                    <span className={cls.username}>
                      @{a.actor?.username || "system"}
                    </span>
                    <span className={cls.time}>
                      {new Date(a.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <div className={cls.metaRow}>
                    <span className={cls.pill}>{a.actionType}</span>
                    <span className={cls.pillMuted}>{a.visibility}</span>
                    <span className={cls.pillMuted}>{a.targetType}</span>
                  </div>

                  {a.meta && Object.keys(a.meta).length > 0 && (
                    <pre className={cls.metaBox}>
                      {JSON.stringify(a.meta, null, 2)}
                    </pre>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ---------- Pagination ---------- */}
      {meta && meta.pages > 1 && (
        <div className={cls.pager}>
          <button
            className="nu-btn nu-btn--ghost"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            Prev
          </button>

          <span className={cls.pageInfo}>
            Page {meta.page} / {meta.pages}
          </span>

          <button
            className="nu-btn nu-btn--ghost"
            disabled={page >= meta.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ClubActivities;
